import React, { useEffect, useMemo } from "react";
import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  StepDefinition,
  StepFieldDefinition,
  applyDerivedRule,
} from "./stepDefinitions";

interface DynamicServiceFormProps {
  definition: StepDefinition;
  config: Record<string, any>;
  onChange: (field: string, value: any) => void;
}

const getValueByPath = (obj: Record<string, any>, path: string): any => {
  if (!path) return undefined;
  return path.split(".").reduce<any>((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
};

const isVisible = (field: StepFieldDefinition, config: Record<string, any>) => {
  if (!field.visibleWhen) return true;
  return getValueByPath(config, field.visibleWhen.field) === field.visibleWhen.equals;
};

const DynamicServiceForm: React.FC<DynamicServiceFormProps> = ({
  definition,
  config,
  onChange,
}) => {
  const theme = useTheme();
  const red = theme.palette.status.accent;
  const fields = useMemo(() => definition.fields || [], [definition.fields]);
  const rules = useMemo(
    () => definition.autoPopulateRules || definition.derivedRules || [],
    [definition.autoPopulateRules, definition.derivedRules]
  );

  useEffect(() => {
    rules.forEach((rule) => {
      const nextValue = applyDerivedRule(rule, config);
      if (getValueByPath(config, rule.target) !== nextValue) {
        onChange(rule.target, nextValue);
      }
    });
  }, [config, onChange, rules]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderLeft: `3px solid ${red}`,
        borderRadius: 2,
        p: 2,
        mt: 1,
        boxShadow: "0 0 6px rgba(0,0,0,0.3)",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ mb: 2, fontWeight: 600, color: red, letterSpacing: 0.3 }}
      >
        {definition.label} Configuration
      </Typography>

      <Grid container spacing={2}>
        {fields
          .filter((field) => !field.hidden && isVisible(field, config))
          .map((field) => {
            if (field.type === "boolean") {
              return (
                <Grid item xs={12} sm={6} key={field.key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={getValueByPath(config, field.key) ?? false}
                        onChange={(e) => onChange(field.key, e.target.checked)}
                        disabled={field.disabled}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: red },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: red,
                          },
                        }}
                      />
                    }
                    label={<Typography sx={{ fontWeight: 500 }}>{field.label}</Typography>}
                  />
                </Grid>
              );
            }

            if (field.type === "select") {
              return (
                <Grid item xs={12} sm={6} key={field.key}>
                  <TextField
                    select
                    label={field.label}
                    fullWidth
                    value={getValueByPath(config, field.key) || ""}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    disabled={field.disabled}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#444" },
                        "&:hover fieldset": { borderColor: red },
                        "&.Mui-focused fieldset": { borderColor: red },
                      },
                    }}
                  >
                    {(field.options || []).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              );
            }

            return (
              <Grid item xs={12} sm={field.key === "apiEndpoint" || field.key === "callBackURL" ? 12 : 6} key={field.key}>
                <TextField
                  label={field.label}
                  InputLabelProps={field.type === "number" ? { shrink: true } : undefined}
                  fullWidth
                  value={getValueByPath(config, field.key) || ""}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  // inputType allows metadata like password while retaining data type
                  inputProps={field.inputMaxLength ? { maxLength: field.inputMaxLength } : undefined}
                  type={field.inputType || (field.type === "number" ? "number" : "text")}
                  disabled={field.disabled}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#444" },
                      "&:hover fieldset": { borderColor: red },
                      "&.Mui-focused fieldset": { borderColor: red },
                    },
                    ...(field.disabled ? { "& .MuiInputBase-input": { color: "#999" } } : {}),
                  }}
                />
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
};

export default DynamicServiceForm;
